/**
 * PropositionSignaturePage — Page publique de signature électronique (SES eIDAS)
 * pour les Propositions ESS BATICO (V36.13 — 28/05/2026).
 *
 * Accessible depuis l'email "Proposition ESS BATICO à signer", sans login.
 * Auth = token signé URL-safe (?devis_id=X&token=Y).
 *
 * Design : gradient violet/indigo aligné avec le template email + branding COPPAM.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getToken } from '../services/tokenService';
import { toast } from 'sonner';
import {
  Loader2, ShieldCheck, FileSignature, Download,
  CheckCircle, AlertTriangle, Lock, Building2, Smartphone,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Message d'erreur affichable (FastAPI peut renvoyer une liste d'erreurs de validation)
const texteErreur = (err, defaut) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ; ') || defaut;
  return defaut;
};

const API = `${BACKEND_URL}/api`;

const fmtEur = (v) => Number(v || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}) + ' €';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return '—'; }
};

export default function PropositionSignaturePage() {
  const [params] = useSearchParams();
  const devisId = params.get('devis_id') || '';
  const qrCode = params.get('c') || '';
  const pourMembre = params.get('pour') === 'membre' && !!getToken();
  const [telPour, setTelPour] = useState('');
  const [token, setToken] = useState(params.get('token') || '');

  const [loading, setLoading] = useState(true);
  const [proposition, setProposition] = useState(null);
  const [status, setStatus] = useState('pending');
  const [error, setError] = useState('');

  // Form state
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [code, setCode] = useState('');
  const [telMasque, setTelMasque] = useState('');
  const [envoiCode, setEnvoiCode] = useState(null);

  useEffect(() => {
    if (!pourMembre || !devisId) return;
    axios.get(`${API}/batico/proposition/${encodeURIComponent(devisId)}/signature-espace`, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => setTelPour(r.data.telephone_membre || '')).catch(() => {});
  }, [pourMembre, devisId]);

  const demanderCode = async (choix) => {
    if (pourMembre && telPour.replace(/\D/g, '').length < 9) { toast.error('Saisissez le numéro de mobile qui recevra le code'); return; }
    setEnvoiCode(choix);
    try {
      const r = await axios.post(`${API}/batico/proposition/${encodeURIComponent(devisId)}/signature-code`,
        { token, canal: choix, ...(pourMembre && { telephone: telPour }) },
        pourMembre ? { headers: { Authorization: `Bearer ${getToken()}` } } : undefined);
      setTelMasque(r.data.telephone_masque);
      toast.success(`Code envoyé par SMS au ${r.data.telephone_masque}`);
    } catch (err) {
      toast.error(texteErreur(err, "Envoi du code impossible"));
    } finally { setEnvoiCode(null); }
  };

  // Post-signature state
  const [signedAt, setSignedAt] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (devisId && !token && qrCode) {
        try {
          const r = await axios.get(`${API}/batico/proposition/${encodeURIComponent(devisId)}/lien-qr`, { params: { c: qrCode } });
          if (!cancelled) setToken(r.data.token);
        } catch (err) {
          if (!cancelled) { setError(texteErreur(err, 'Lien de signature invalide')); setLoading(false); }
        }
        return;
      }
      if (!devisId || !token) {
        setError('Lien invalide : devis_id ou token manquant');
        setLoading(false);
        return;
      }
      try {
        // 1. Status (vérifie token + détecte déjà signé)
        const st = await axios.get(
          `${API}/batico/proposition/${encodeURIComponent(devisId)}/signature-status`,
          { params: { token } },
        );
        if (cancelled) return;
        setStatus(st.data?.status || 'pending');
        if (st.data?.signed_at_iso) setSignedAt(st.data.signed_at_iso);

        // 2. Snapshot (récap proposition)
        const sn = await axios.get(
          `${API}/batico/proposition/${encodeURIComponent(devisId)}`,
          { params: { token } },
        );
        if (cancelled) return;
        const snap = sn.data?.snapshot || {};
        const tar = snap.tarification || {};
        const prefill = snap.membre_prefill || {};
        setProposition({
          raison_sociale: prefill.raison_sociale || snap.raison_sociale || '—',
          montant_ttc: tar.montant_ttc || snap.montant_ttc || 0,
          montant_ht: tar.montant_ht || snap.montant_ht || 0,
          expires_at: sn.data?.expires_at || '',
          validite_jours: sn.data?.validite_jours || 365,
          type_souscription: snap.type_souscription || 'annuel',
        });
        // Pré-remplir email du membre si disponible
        const memberEmail = prefill.email || snap.email || '';
        if (memberEmail) setEmail(memberEmail);
        const memberName = prefill.raison_sociale
          || (prefill.prenom && prefill.nom ? `${prefill.prenom} ${prefill.nom}` : '');
        if (memberName) setFullName(memberName);
      } catch (e) {
        if (cancelled) return;
        const code = e?.response?.status;
        const detail = typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : '';
        if (code === 422) setError('Lien incomplet — rouvrez le lien reçu ou scannez à nouveau le QR code');
        else if (code === 403) setError(detail || 'Lien invalide ou token incorrect');
        else if (code === 410) setError(detail || 'Proposition expirée — demandez un nouveau lien');
        else if (code === 404) setError(detail || 'Proposition introuvable — vérifiez le lien reçu par email');
        else setError(detail || 'Erreur de chargement de la proposition');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [devisId, token]);

  const handleSign = async (e) => {
    e?.preventDefault?.();
    if (!fullName.trim() || fullName.trim().length < 2) {
      toast.error('Nom complet requis (min. 2 caractères)');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('Adresse email invalide');
      return;
    }
    if (!cgvAccepted) {
      toast.error('Cochez « Bon pour accord » pour signer');
      return;
    }
    if (!/^\d{6}$/.test(code.trim())) {
      toast.error('Saisissez le code à 6 chiffres reçu par SMS');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/batico/proposition/${encodeURIComponent(devisId)}/sign-public`,
        {
          token,
          signer_full_name: fullName.trim(),
          signer_email: email.trim().toLowerCase(),
          cgv_accepted: true,
          otp_code: code.trim(),
        },
        pourMembre ? { headers: { Authorization: `Bearer ${getToken()}` } } : undefined,
      );
      if (res.data?.ok) {
        setStatus('signed');
        setSignedAt(res.data.signed_at_iso || '');
        setDownloadUrl(
          res.data.download_url
            ? `${BACKEND_URL}${res.data.download_url}`
            : '',
        );
        toast.success(
          res.data.already_signed
            ? 'Proposition déjà signée'
            : 'Signature enregistrée avec succès',
        );
      } else {
        toast.error('Signature échouée');
      }
    } catch (err) {
      toast.error(texteErreur(err, 'Erreur lors de la signature'));
    } finally {
      setSubmitting(false);
    }
  };

  // === Loading / Error states ===
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-purple-50/40 to-indigo-50/40">
        <div className="text-center text-slate-600">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3 text-purple-600" />
          Chargement de la proposition…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-red-50/30 to-orange-50/30 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="signature-error">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Lien non valide</h2>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <p className="text-xs text-slate-500">
            Vérifiez que vous utilisez bien le lien reçu par email,
            ou contactez l'émetteur de la proposition.
          </p>
        </div>
      </div>
    );
  }

  // === Already signed - confirmation ===
  const isSigned = status === 'signed';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/40 to-indigo-50/40 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* HEADER GRADIENT COPPAM */}
        <div
          className="relative overflow-hidden rounded-2xl shadow-2xl text-white mb-6"
          style={{
            background: 'linear-gradient(135deg, #7c3aed 0%, #4338ca 100%)',
          }}
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full blur-3xl -translate-y-32 translate-x-32" />
          </div>
          <div className="relative px-6 sm:px-10 py-8">
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-white/85 mb-3">
              <span className="inline-block w-7 h-px bg-amber-300/80" />
              <span className="font-semibold">COPPAM</span>
              <span className="text-white/40">|</span>
              <span>O'SCOP Outremer</span>
              <span className="text-white/40">|</span>
              <span>Signature électronique sécurisée</span>
            </div>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.05]"
              style={{ fontFamily: '"Playfair Display", "Georgia", serif' }}
            >
              {isSigned ? 'Proposition signée' : 'Signature de la proposition'}
            </h1>
            <div className="mt-3 text-sm text-white/80">
              Référence&nbsp;:{' '}
              <span className="font-mono bg-white/15 px-2 py-0.5 rounded text-xs">
                {devisId}
              </span>
            </div>
          </div>
        </div>

        {/* RÉCAPITULATIF */}
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6 border border-slate-200/60">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-indigo-600" />
            Récapitulatif
          </h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            <div>
              <dt className="text-slate-500 text-xs uppercase tracking-wider">Raison sociale</dt>
              <dd className="font-semibold text-slate-900" data-testid="sig-raison-sociale">
                {proposition?.raison_sociale}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 text-xs uppercase tracking-wider">Type</dt>
              <dd className="font-semibold text-slate-900">
                {proposition?.type_souscription === 'nominative'
                  ? 'RCDS Nominative'
                  : 'RCDS Annuelle'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 text-xs uppercase tracking-wider">Montant TTC</dt>
              <dd className="text-2xl font-extrabold text-indigo-700" data-testid="sig-montant">
                {fmtEur(proposition?.montant_ttc)}
              </dd>
            </div>
            <div>
              <dt className="text-slate-500 text-xs uppercase tracking-wider">Valide jusqu'au</dt>
              <dd className="font-semibold text-slate-900">
                {fmtDate(proposition?.expires_at)}
              </dd>
            </div>
          </dl>
        </div>

        {/* FORMULAIRE DE SIGNATURE ou CONFIRMATION */}
        {isSigned ? (
          <div
            className="bg-white rounded-2xl shadow-lg p-8 border-2 border-emerald-200"
            data-testid="signature-success-card"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                <CheckCircle className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Signature enregistrée
              </h2>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                Votre signature a été enregistrée le{' '}
                <strong>
                  {signedAt
                    ? new Date(signedAt).toLocaleString('fr-FR', {
                        day: '2-digit', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : ''}
                </strong>
                . L'équipe O'SCOP a été notifiée et reviendra vers vous pour la
                validation finale.
              </p>
            </div>

            {(downloadUrl
              || `${BACKEND_URL}/api/batico/proposition/${encodeURIComponent(devisId)}/signed-pdf?token=${encodeURIComponent(token)}`) && (
              <div className="text-center">
                <a
                  href={downloadUrl
                    || `${BACKEND_URL}/api/batico/proposition/${encodeURIComponent(devisId)}/signed-pdf?token=${encodeURIComponent(token)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="signature-download-pdf"
                >
                  <Button
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-12 px-8 text-base font-semibold shadow-lg"
                  >
                    <Download className="mr-2 h-5 w-5" />
                    Télécharger le PDF signé
                  </Button>
                </a>
                <p className="text-xs text-slate-500 mt-3">
                  Le PDF inclut un certificat de signature électronique (preuves eIDAS).
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 border border-slate-200/60">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-purple-600" />
              Vos informations
            </h2>

            <form onSubmit={handleSign} className="space-y-4">
              <div>
                <Label htmlFor="sign-fullname" className="text-sm font-medium text-slate-700">
                  Nom complet du signataire <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="sign-fullname"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="ex. Marie DUPONT"
                  disabled={submitting}
                  className="mt-1 h-11"
                  maxLength={120}
                  data-testid="signature-fullname-input"
                />
              </div>

              <div>
                <Label htmlFor="sign-email" className="text-sm font-medium text-slate-700">
                  Adresse email <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="sign-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@exemple.fr"
                  disabled={submitting}
                  className="mt-1 h-11"
                  data-testid="signature-email-input"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Le PDF signé et la confirmation seront envoyés à cette adresse.
                </p>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={cgvAccepted}
                    onCheckedChange={(v) => setCgvAccepted(!!v)}
                    disabled={submitting}
                    className="mt-0.5"
                    data-testid="signature-cgv-checkbox"
                  />
                  <span className="text-sm text-slate-700 leading-relaxed">
                    <strong className="block text-base text-slate-900">Bon pour accord</strong>
                    J'accepte les{' '}
                    <a
                      href="/cgu"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-700 underline hover:text-purple-900"
                    >
                      conditions générales
                    </a>
                    {' '}et confirme signer électroniquement la Proposition ESS{' '}
                    <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                      {devisId}
                    </span>
                    {' '}pour un montant TTC de{' '}
                    <strong>{fmtEur(proposition?.montant_ttc)}</strong>.
                  </span>
                </label>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid="signature-otp-bloc">
                <p className="text-sm text-slate-700">
                  Pour confirmer votre identité, recevez un code à usage unique sur votre mobile.
                </p>
                {pourMembre && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-2" data-testid="signature-pour-membre-bloc">
                    <label className="text-xs font-semibold text-amber-900">Signature pour le compte du membre — mobile qui recevra le code</label>
                    <Input value={telPour} onChange={(e) => setTelPour(e.target.value)} placeholder="0696 12 34 56"
                      inputMode="tel" className="mt-1 h-9 bg-white" data-testid="signature-pour-membre-tel" />
                    <p className="mt-1 text-[11px] text-amber-800">Prérempli avec le mobile du membre ; le numéro saisi n'est pas enregistré dans sa fiche.</p>
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button type="button" variant="outline" onClick={() => demanderCode('sms')} disabled={!!envoiCode || submitting}
                    className="border-purple-300 text-purple-700" data-testid="signature-envoyer-code-btn">
                    {envoiCode === 'sms' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Smartphone className="mr-2 h-4 w-4" />}
                    {telMasque ? 'Renvoyer le code' : 'Recevoir mon code par SMS'}
                  </Button>
                  <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Code SMS" inputMode="numeric" disabled={!telMasque || submitting}
                    className="h-10 sm:w-44 font-mono tracking-[0.3em]" data-testid="signature-code-input" />
                </div>
                {telMasque && <p className="text-xs text-slate-500" data-testid="signature-tel-masque">Code envoyé par SMS au {telMasque} — valable 10 minutes. Pas reçu ? Renvoyez-le au bout d'une minute.</p>}
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-400 rounded p-3 text-xs text-purple-900">
                <div className="flex items-start gap-2">
                  <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <strong>Signature Électronique Simple (SES eIDAS)</strong>
                    {' '}— Conforme au Règlement européen 910/2014. L'horodatage UTC, votre adresse IP
                    l'empreinte cryptographique du document et la vérification par code SMS seront enregistrés à des fins de preuve.
                    Aucune impression n'est nécessaire : le devis signé est transmis à votre COOPER'S et à l'administration.
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={submitting || !cgvAccepted || code.length !== 6}
                className="w-full h-12 text-base font-bold bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg disabled:opacity-50"
                data-testid="signature-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signature en cours…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="mr-2 h-5 w-5" />
                    Je signe la proposition
                  </>
                )}
              </Button>
            </form>
          </div>
        )}

        {/* FOOTER */}
        <div className="text-center mt-8 text-xs text-slate-500">
          <p className="font-semibold text-slate-700 mb-1">COPPAM · O'SCOP Outremer</p>
          <p>Coopérative Multisectorielle &amp; Solidaire des Outre-mer</p>
        </div>
      </div>
    </div>
  );
}
